import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, ExclamationTriangleIcon, CurrencyDollarIcon, GiftIcon,
  InformationCircleIcon, ArrowPathIcon, HomeIcon, EyeIcon, PlusIcon
} from '@heroicons/react/24/outline';
import SectorBreakdownChart from './SectorBreakdownChart';
import TopHoldingsChart from './TopHoldingsChart';
import RiskScoreCard from './RiskScoreCard';
import FeeAnalysisCard from './FeeAnalysisCard';
import DividendOutlookCard from './DividendOutlookCard';
import AIExplanationCard from './AIExplanationCard';
import VisionPortfolioInsights from './VisionPortfolioInsights';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PortfolioXRay = ({ user }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portfolioConnected, setPortfolioConnected] = useState(false);
  const [activeTopic, setActiveTopic] = useState('visions');
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.user_id) {
      checkPortfolioConnection();
      fetchPortfolioAnalysis();
    }
    // eslint-disable-next-line
  }, [user?.user_id]);

  const checkPortfolioConnection = async () => {
    try {
      const response = await axios.get('/api/portfolio/accounts');
      setPortfolioConnected(true);
    } catch {
      setPortfolioConnected(false);
    }
  };

  const fetchPortfolioAnalysis = async () => {
    setLoading(true); setError(null);
    try {
      const response = await axios.get('/api/portfolio/x-ray');
      setAnalysisData(response.data);
    } catch {
      setError('Failed to load portfolio analysis. Please try again.');
      setAnalysisData(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalysis = () => {
    checkPortfolioConnection();
    fetchPortfolioAnalysis();
  };

  const status = analysisData?.status ?? "ready";
  const totalValue = analysisData?.total_value ?? 0;
  const isPortfolioConnected = portfolioConnected || user?.portfolio_connected;
  const loadingUI = loading || status === "pending";
  const isPortfolioEmpty = !loadingUI && totalValue === 0 && status === "ready";

  // Loading
  if (loadingUI) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-8 w-8 text-pine animate-spin mx-auto mb-4" />
          <p className="text-primary text-lg">Analyzing your portfolio...</p>
          <p className="text-secondary text-sm mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }
  // Error
  if (error || status === "error") {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6 surface-panel">
          <ExclamationTriangleIcon className="h-12 w-12 text-danger mx-auto mb-4" />
          <h3 className="text-xl font-display font-semibold text-primary mb-2">Analysis Failed</h3>
          <p className="text-secondary mb-4">{error || "Portfolio analysis failed: server error."}</p>
          <button
            onClick={refreshAnalysis}
            className="btn-primary"
          >Try Again</button>
        </div>
      </div>
    );
  }
  // Empty state
  if (isPortfolioEmpty) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6 surface-panel">
          <ChartBarIcon className="h-12 w-12 text-secondary mx-auto mb-4" />
          <h3 className="text-xl font-display font-semibold text-primary mb-2">
            No Investment Holdings Found
          </h3>
          <p className="text-secondary mb-4">
            Your accounts are connected, but Portfolio X-Ray analyzes investment holdings like stocks, ETFs, and mutual funds. 
            While 401k accounts typically contain investments, your current accounts may not have individual holdings available through the current connection. 
            Try connecting a brokerage account or investment platform for detailed analysis.
          </p>
          <div className="space-y-3">
            <button
              onClick={refreshAnalysis}
              className="btn-primary w-full"
            >
              Refresh Analysis
            </button>
            <button
              onClick={() => navigate('/onboarding')}
              className="btn-secondary w-full"
            >
              Connect Investment Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <div className="surface-emphasis p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-h1 font-display">Portfolio X-Ray</h1>
              <p className="text-secondary mt-1">
                Deep insights into your investment portfolio
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')}
                className="btn-secondary px-3 py-2 flex items-center gap-2">
                <HomeIcon className="h-4 w-4" /> Dashboard
              </button>
              <button onClick={() => navigate('/visions')}
                className="btn-secondary px-3 py-2 flex items-center gap-2">
                <EyeIcon className="h-4 w-4" /> Visions
              </button>
              <button onClick={() => navigate('/onboarding')}
                className="btn-secondary px-3 py-2 flex items-center gap-2">
                <PlusIcon className="h-4 w-4" /> Add Account
              </button>
              <button onClick={refreshAnalysis}
                className="btn-primary px-4 py-2 flex items-center gap-2">
                <ArrowPathIcon className="h-4 w-4" /> Refresh
              </button>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="light-surface surface-panel p-4">
              <p className="text-secondary text-sm">Total Value</p>
              <p className="figure text-primary text-2xl font-semibold">${totalValue.toLocaleString()}</p>
            </div>
            <div className="light-surface surface-panel p-4">
              <p className="text-secondary text-sm">Risk Score</p>
              <p className="figure text-primary text-2xl font-semibold">{analysisData?.risk_score ?? 'N/A'}/10</p>
            </div>
            <div className="light-surface surface-panel p-4">
              <p className="text-secondary text-sm">Annual Fees</p>
              <p className="figure text-primary text-2xl font-semibold">${analysisData?.estimated_annual_fees ?? 0}</p>
            </div>
            <div className="light-surface surface-panel p-4">
              <p className="text-secondary text-sm">Dividend Income</p>
              <p className="figure text-primary text-2xl font-semibold">${analysisData?.estimated_dividend_income ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-hairline bg-bone-raised">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 py-4">
            {[
              { id: 'visions', icon: EyeIcon, label: 'Portfolio Impact on Visions' },
              { id: 'holdings', icon: InformationCircleIcon, label: 'Top Holdings' },
              { id: 'sectors', icon: ChartBarIcon, label: 'Sector Allocation' },
              { id: 'risk', icon: ExclamationTriangleIcon, label: 'Risk Analysis' },
              { id: 'fees', icon: CurrencyDollarIcon, label: 'Investment Costs' },
              { id: 'dividends', icon: GiftIcon, label: 'Dividend Outlook' },
            ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTopic(id)}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors duration-300 flex items-center gap-2 ${
                activeTopic === id
                  ? 'bg-pine surface-dark'
                  : 'btn-secondary border-hairline'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
            ))}
          </div>
        </div>
      </div>

      {/* Topic Content */}
      <div className="max-w-7xl mx-auto p-6">
        <motion.div
          key={activeTopic}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {activeTopic === 'visions' && (
            <>
              <AIExplanationCard tabName="overview" analysisData={analysisData} />
              <VisionPortfolioInsights user={user} portfolioData={analysisData} />
            </>
          )}

          {activeTopic === 'holdings' && (
            <div className="surface-panel p-6">
              <h2 className="text-xl font-display font-semibold text-primary mb-4 flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-pine" /> Top Holdings
              </h2>
              <TopHoldingsChart data={analysisData?.top_holdings ?? []} />
            </div>
          )}

          {activeTopic === 'sectors' && (
            <div className="surface-panel p-6">
              <h2 className="text-xl font-display font-semibold text-primary mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-pine" /> Sector Allocation
              </h2>
              <SectorBreakdownChart data={analysisData?.sector_allocation ?? []} />
            </div>
          )}

          {activeTopic === 'risk' && (
            <>
              <AIExplanationCard tabName="risk" analysisData={analysisData} />
              <RiskScoreCard
                riskScore={analysisData?.risk_score}
                explanation={analysisData?.risk_explanation}
                detailed={true}
                concentrationMetrics={analysisData?.concentration_metrics}
              />
            </>
          )}

          {activeTopic === 'fees' && (
            <>
              <AIExplanationCard tabName="fees" analysisData={analysisData} />
              <FeeAnalysisCard
                totalFees={analysisData?.estimated_annual_fees}
                feeBreakdown={analysisData?.fee_breakdown}
                totalValue={totalValue}
                detailed={true}
              />
            </>
          )}

          {activeTopic === 'dividends' && (
            <>
              <AIExplanationCard tabName="dividends" analysisData={analysisData} />
              <DividendOutlookCard
                dividendIncome={analysisData?.estimated_dividend_income}
                dividendSchedule={analysisData?.dividend_schedule}
                totalValue={totalValue}
              />
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PortfolioXRay;
